import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FONT_LIBRARY,
  FONT_WEIGHT_LABELS,
  FONT_SIZES,
  FONT_SCALE,
  getFont,
  loadAllFonts,
} from "@/lib/fonts";

/**
 * FontPicker — inline control used in game editors to override the font of a
 * public-game element. value = { family?, weight?, size? } | null.
 * Null / missing parts fall back to the theme defaults.
 */
export default function FontPicker({
  label,
  value,
  onChange,
  previewText,
  testIdPrefix,
}) {
  const { t } = useTranslation();
  useEffect(() => {
    loadAllFonts();
  }, []);

  const cfg = value || {};
  const familyId = cfg.family || "default";
  const font = getFont(familyId);
  const weight = cfg.weight || null;
  const size = cfg.size || "md";
  const isSet = Boolean(font || cfg.weight || (cfg.size && cfg.size !== "md"));

  function update(next) {
    const merged = { ...cfg, ...next };
    Object.keys(merged).forEach((k) => {
      if (merged[k] == null) delete merged[k];
    });
    if (merged.size === "md") delete merged.size;
    onChange(Object.keys(merged).length ? merged : null);
  }

  const SIZE_LABELS = {
    sm: t("game_editor.size_sm", "Petite"),
    md: t("game_editor.size_md", "Normale"),
    lg: t("game_editor.size_lg", "Grande"),
    xl: t("game_editor.size_xl", "Très grande"),
  };

  return (
    <div data-testid={`${testIdPrefix}-block`}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5">
        {label}
      </div>
      <Select
        value={familyId}
        onValueChange={(v) => {
          if (v === "default") return update({ family: null, weight: null });
          const f = getFont(v);
          const w = weight && f?.weights?.includes(weight) ? weight : null;
          update({ family: v, weight: w });
        }}
      >
        <SelectTrigger
          className="h-9 rounded-lg text-[13px]"
          data-testid={`${testIdPrefix}-family`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="default">
            {t("game_editor.font_default", "Par défaut (thème)")}
          </SelectItem>
          {FONT_LIBRARY.map((f) => (
            <SelectItem key={f.id} value={f.id} style={{ fontFamily: f.stack }}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="mt-1.5 grid grid-cols-2 gap-1.5">
        {font ? (
          <Select
            value={weight ? String(weight) : "auto"}
            onValueChange={(v) =>
              update({ weight: v === "auto" ? null : Number(v) })
            }
          >
            <SelectTrigger
              className="h-8 rounded-lg text-[12px]"
              data-testid={`${testIdPrefix}-weight`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">
                {t("game_editor.font_weight_auto", "Graisse auto")}
              </SelectItem>
              {(font.weights || []).map((w) => (
                <SelectItem
                  key={w}
                  value={String(w)}
                  style={{ fontFamily: font.stack, fontWeight: w }}
                >
                  {FONT_WEIGHT_LABELS[w] || w} ({w})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div />
        )}
        <Select
          value={size}
          onValueChange={(v) => update({ size: v === "md" ? null : v })}
        >
          <SelectTrigger
            className="h-8 rounded-lg text-[12px]"
            data-testid={`${testIdPrefix}-size`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {SIZE_LABELS[s.id]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div
        className="mt-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2 text-sm truncate"
        style={
          isSet
            ? {
                fontFamily: font?.stack,
                fontWeight: weight || undefined,
                fontSize:
                  FONT_SCALE[size] !== 1 ? `${FONT_SCALE[size]}em` : undefined,
              }
            : undefined
        }
        data-testid={`${testIdPrefix}-preview`}
      >
        {previewText}
      </div>
      {isSet ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground underline underline-offset-4"
          data-testid={`${testIdPrefix}-reset`}
        >
          {t("game_editor.color_reset", "Réinitialiser par défaut")}
        </button>
      ) : (
        <div className="mt-1.5 text-[11px] text-muted-foreground">
          {t("game_editor.font_using_default", "Police du thème utilisée")}
        </div>
      )}
    </div>
  );
}
